import { useState, type FormEvent } from "react";
import { KeyRound, LogOut, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ConnectionRpcError,
  useConnectionActions,
  useConnectionSession,
  useMtprotoSession,
} from "@/lib/hooks/useConnections";
import {
  buildE164,
  connectionErrorMessage,
  deriveChannelState,
  isDroppedSession,
  isProcessing,
  onlyDigits,
  type ConnectionSession,
} from "@/lib/connections";
import { CardShell, ChannelStatusBadge, LoadError, Notice, PhoneField, Processing, StepList } from "./shared";

type Actions = ReturnType<typeof useConnectionActions>;

function rpcCode(error: Error | null): string | null {
  if (!error) return null;
  return error instanceof ConnectionRpcError ? error.code : "unknown";
}

export function TelegramConnectionCard(): JSX.Element {
  const rowQuery = useMtprotoSession();
  const sessionQuery = useConnectionSession("telegram");
  const row = rowQuery.data;
  const session = sessionQuery.data;
  const loadFailed = rowQuery.isError || sessionQuery.isError;
  const retry = (): void => {
    void rowQuery.refetch();
    void sessionQuery.refetch();
  };
  const actions = useConnectionActions("telegram");
  const state = loadFailed ? "error" : deriveChannelState(row, session);
  const failure =
    session?.status === "failed" ? connectionErrorMessage(session.error, session.retry_after_seconds) : null;
  const dropped = isDroppedSession(row) ? "A sessão do Telegram caiu. Conecte de novo para voltar a monitorar." : null;

  return (
    <CardShell
      id="card-telegram-userbot"
      icon={Send}
      title="Telegram (sua conta)"
      badge={<ChannelStatusBadge state={state} />}
    >
      {loadFailed ? (
        <LoadError onRetry={retry} retrying={rowQuery.isFetching || sessionQuery.isFetching} />
      ) : (
        <>
          {state === "connecting" && session && <TelegramLoginSteps session={session} actions={actions} />}
          {state === "connected" && <TelegramConnected phone={row?.phone ?? null} actions={actions} />}
          {(state === "disconnected" || state === "error") && (
            <TelegramStart actions={actions} notice={failure ?? dropped} />
          )}
        </>
      )}
    </CardShell>
  );
}

function TelegramStart({ actions, notice }: { actions: Actions; notice: string | null }): JSX.Element {
  const [countryCode, setCountryCode] = useState("+55");
  const [phone, setPhone] = useState("");
  const e164 = buildE164(countryCode, phone);
  const error = rpcCode(actions.start.error);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (e164) actions.start.mutate({ phone: e164 });
  };

  return (
    <form className="space-y-4" onSubmit={submit}>
      <p className="text-sm text-muted-foreground">
        Conecte a sua conta do Telegram para o LucraLink ler os canais e grupos que você acompanha e repostar as ofertas
        com o seu link de afiliado.
      </p>
      <StepList
        steps={[
          "Deixe o Telegram aberto no celular ou no computador.",
          "Vamos enviar um código de 5 dígitos para o próprio app do Telegram (não chega por SMS).",
          "Se a sua conta tiver verificação em duas etapas, pediremos a senha em seguida.",
        ]}
      />
      {notice && <Notice tone="danger">{notice}</Notice>}
      <PhoneField
        idPrefix="tg"
        countryCode={countryCode}
        phone={phone}
        onCountryChange={setCountryCode}
        onPhoneChange={setPhone}
        disabled={actions.start.isPending}
      />
      {error && <Notice tone="danger">{connectionErrorMessage(error)}</Notice>}
      <div className="flex justify-end">
        <Button type="submit" variant="secondary" disabled={!e164 || actions.start.isPending}>
          <Send size={15} />
          Enviar código
        </Button>
      </div>
    </form>
  );
}

function TelegramLoginSteps({ session, actions }: { session: ConnectionSession; actions: Actions }): JSX.Element {
  const stepError = session.error ? connectionErrorMessage(session.error, session.retry_after_seconds) : null;
  return (
    <div className="space-y-4">
      {session.status === "requested" && <Processing>Enviando o código para o seu Telegram...</Processing>}
      {session.status === "awaiting_code" && <CodeForm session={session} actions={actions} />}
      {session.status === "awaiting_password" && <PasswordForm session={session} actions={actions} />}
      {stepError && session.status !== "requested" && <Notice tone="danger">{stepError}</Notice>}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => actions.cancel.mutate({ sessionId: session.id })}
          disabled={actions.cancel.isPending}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function CodeForm({ session, actions }: { session: ConnectionSession; actions: Actions }): JSX.Element {
  const [code, setCode] = useState("");
  const busy = isProcessing(session) || actions.submitCode.isPending;
  const error = rpcCode(actions.submitCode.error);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (code.length >= 5) actions.submitCode.mutate({ sessionId: session.id, code });
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <p className="text-sm text-muted-foreground">
        Enviamos um código para o Telegram de {session.phone_masked ?? "seu número"}. Abra a conversa
        <strong className="text-foreground"> Telegram</strong> no app e digite o código abaixo.
      </p>
      <div className="space-y-2">
        <Label htmlFor="tg-code" variant="field">
          Código de 5 dígitos
        </Label>
        <Input
          id="tg-code"
          value={code}
          onChange={(e) => setCode(onlyDigits(e.target.value, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="12345"
          className="max-w-[12rem] font-mono text-lg tracking-[0.3em]"
          disabled={busy}
        />
      </div>
      {error && <Notice tone="danger">{connectionErrorMessage(error)}</Notice>}
      {busy ? (
        <Processing>Confirmando o código...</Processing>
      ) : (
        <Button type="submit" variant="secondary" disabled={code.length < 5}>
          Confirmar código
        </Button>
      )}
    </form>
  );
}

function PasswordForm({ session, actions }: { session: ConnectionSession; actions: Actions }): JSX.Element {
  const [password, setPassword] = useState("");
  const busy = isProcessing(session) || actions.submitPassword.isPending;
  const error = rpcCode(actions.submitPassword.error);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (password) actions.submitPassword.mutate({ sessionId: session.id, password });
    setPassword("");
  };

  return (
    <form className="space-y-3" onSubmit={submit}>
      <p className="text-sm text-muted-foreground">
        Sua conta tem verificação em duas etapas. Digite a senha que você criou no Telegram. Ela é usada só para concluir a
        conexão e não fica guardada.
      </p>
      <div className="space-y-2">
        <Label htmlFor="tg-password" variant="field">
          Senha de duas etapas
        </Label>
        <Input
          id="tg-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className="max-w-xs"
          disabled={busy}
        />
      </div>
      {error && <Notice tone="danger">{connectionErrorMessage(error)}</Notice>}
      {busy ? (
        <Processing>Verificando a senha...</Processing>
      ) : (
        <Button type="submit" variant="secondary" disabled={!password}>
          <KeyRound size={15} />
          Concluir conexão
        </Button>
      )}
    </form>
  );
}

function TelegramConnected({ phone, actions }: { phone: string | null; actions: Actions }): JSX.Element {
  const confirmAndDisconnect = (): void => {
    if (window.confirm("Desconectar a sua conta do Telegram? O monitoramento dos canais para até você conectar de novo.")) {
      actions.disconnect.mutate();
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="min-w-0 flex-1 font-mono text-sm text-foreground">{phone ?? "Conta conectada"}</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={confirmAndDisconnect}
        disabled={actions.disconnect.isPending}
        className="gap-1.5 text-destructive hover:text-destructive"
      >
        <LogOut size={14} />
        {actions.disconnect.isPending ? "Desconectando..." : "Desconectar"}
      </Button>
      {actions.disconnect.error && <Notice tone="danger">Não foi possível desconectar agora. Tente de novo.</Notice>}
    </div>
  );
}
